/**
 * Unified Notification System - Type Definitions
 *
 * This module defines the types and classification rules for all notifications.
 * Notifications are classified as either persistent (stored in DB) or ephemeral (toast-only).
 */

import { NotificationCategory, NotificationBehavior } from '@/store/slices/notificationsTypes';

// Priority levels for notifications
export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

// Toast visual styles
export type ToastStyle = 'success' | 'error' | 'info' | 'loading' | 'warning';

/**
 * Notification Scope - determines where notifications are visible
 *
 * - 'global': Visible everywhere, persists in DB, survives page navigation and sessions
 *   Example: "Your account quota is at 90%", "New feature available"
 *
 * - 'session': Shared across related pages in a workflow, stored in sessionStorage/Redux
 *   Example: Call flow (call page → incident report → reports) shares callId-scoped notifications
 *   Cleared when session ends (browser close or explicit clear)
 *
 * - 'page': Only visible on current page, dismissed on navigation
 *   Example: "Copied to clipboard", "Settings saved"
 */
export type NotificationScope = 'global' | 'session' | 'page';

/**
 * Session types for grouping related pages
 */
export type SessionType = 'call' | 'report' | 'settings' | 'custom';

/**
 * Session definition for scope: 'session' notifications
 */
export interface NotificationSession {
  type: SessionType;
  id: string;  // e.g., callId, reportId
}

/**
 * Full notification options for the unified system.
 */
export interface NotifyOptions {
  /** Optional mounted toast destination; persistence and Bell delivery stay scope-based. */
  toasterId?: string;
  // Content
  title: string;
  body?: string;
  icon?: string;

  // Classification
  category: NotificationCategory;
  priority?: NotificationPriority;
  behavior?: NotificationBehavior;

  // Scoping - where the notification is visible
  scope?: NotificationScope;     // 'global' | 'session' | 'page' (default: 'page')
  session?: NotificationSession; // Required when scope is 'session'

  // Behavior (legacy - use scope instead for new code)
  persistent?: boolean;         // DEPRECATED: Use scope: 'global' instead
  dedupeKey?: string;           // Key for deduplication (e.g., 'recording-start')
  dedupeWindowMs?: number;      // Time window for dedup (default: 5000ms)

  // Toast options (for ephemeral or immediate feedback)
  showToast?: boolean;          // Show toast even if persistent (default: true)
  toastDuration?: number;       // Override default duration in ms
  toastStyle?: ToastStyle;      // Visual style of the toast

  // Action
  actionUrl?: string;
  actionLabel?: string;

  // Metadata
  metadata?: Record<string, unknown>;
}

/**
 * Shorthand options for quick notification methods.
 */
export interface ShorthandOptions extends Omit<NotifyOptions, 'title' | 'category' | 'toastStyle'> {
  category?: NotificationCategory;
}

/**
 * Determines effective scope from options.
 * Handles legacy 'persistent' flag for backwards compatibility.
 */
export function getEffectiveScope(options: Partial<NotifyOptions>): NotificationScope {
  // New scope field takes precedence
  if (options.scope) {
    return options.scope;
  }

  // Legacy: persistent: true → global scope
  if (options.persistent === true) {
    return 'global';
  }

  // Default: page scope (toast only)
  return 'page';
}

/**
 * Check if a notification should be stored (not just toasted)
 */
export function shouldStore(scope: NotificationScope): boolean {
  return scope === 'global' || scope === 'session';
}

/**
 * Result of deduplication check.
 */
export interface DedupeResult {
  show: boolean;          // Should the notification be shown?
  groupCount?: number;    // If grouped, how many in the group?
  isGrouped?: boolean;    // Is this a grouped/summary notification?
}

/**
 * Result of rate limit check.
 */
export interface RateLimitResult {
  allowed: boolean;       // Is the notification allowed through?
  queuePosition?: number; // If not allowed, position in queue
  reason?: string;        // Why it was blocked
}

/**
 * Configuration for rate limiting.
 */
export interface RateLimitConfig {
  maxPerMinute: number;       // Max notifications per minute total
  maxPerCategory: number;     // Max per category per minute
  burstAllowance: number;     // Allow burst for urgent/high priority
}

/**
 * Rule definition for auto-classification.
 */
export interface NotificationRule {
  persistent: boolean;
  priority: NotificationPriority;
  defaultToastStyle?: ToastStyle;
  behavior?: NotificationBehavior;
  expiryDays?: number | null; // null = never expires
}

/**
 * Classification rules by event key.
 *
 * These rules determine whether a notification should be persisted
 * and what priority it should have based on its type.
 */
export const NOTIFICATION_RULES: Record<string, NotificationRule> = {
  // Success operations - ephemeral (toast only)
  'recording.started': { persistent: false, priority: 'normal', defaultToastStyle: 'success' },
  'recording.stopped': { persistent: false, priority: 'normal', defaultToastStyle: 'success' },
  'recording.paused': { persistent: false, priority: 'low', defaultToastStyle: 'info' },
  'recording.resumed': { persistent: false, priority: 'low', defaultToastStyle: 'info' },
  'file.shared': { persistent: false, priority: 'low', defaultToastStyle: 'success' },
  'file.uploaded': { persistent: false, priority: 'low', defaultToastStyle: 'success' },
  'login.success': { persistent: false, priority: 'low', defaultToastStyle: 'success' },
  'logout.success': { persistent: false, priority: 'low', defaultToastStyle: 'info' },
  'copy.success': { persistent: false, priority: 'low', defaultToastStyle: 'success' },
  'save.success': { persistent: false, priority: 'low', defaultToastStyle: 'success' },
  'invite.sent': { persistent: false, priority: 'low', defaultToastStyle: 'success' },
  'caller.removed': { persistent: false, priority: 'low', defaultToastStyle: 'success' },
  'caller.joined': { persistent: false, priority: 'low', defaultToastStyle: 'info' },
  'caller.left': { persistent: false, priority: 'low', defaultToastStyle: 'info' },
  'mode.changed': { persistent: false, priority: 'low', defaultToastStyle: 'info' },

  // Errors - persistent (stored in DB)
  'recording.failed': { persistent: true, priority: 'high', defaultToastStyle: 'error', behavior: 'actionable', expiryDays: 7 },
  'connection.lost': { persistent: true, priority: 'urgent', defaultToastStyle: 'error', behavior: 'actionable', expiryDays: 7 },
  'connection.failed': { persistent: true, priority: 'high', defaultToastStyle: 'error', behavior: 'actionable', expiryDays: 7 },
  'auth.failed': { persistent: true, priority: 'high', defaultToastStyle: 'error', behavior: 'actionable', expiryDays: 7 },
  'save.failed': { persistent: true, priority: 'high', defaultToastStyle: 'error', behavior: 'actionable', expiryDays: 7 },
  'upload.failed': { persistent: true, priority: 'high', defaultToastStyle: 'error', behavior: 'actionable', expiryDays: 7 },
  'invite.failed': { persistent: true, priority: 'normal', defaultToastStyle: 'error', behavior: 'actionable', expiryDays: 7 },
  'caller.remove_failed': { persistent: true, priority: 'normal', defaultToastStyle: 'error', behavior: 'actionable', expiryDays: 7 },
  'api.error': { persistent: true, priority: 'high', defaultToastStyle: 'error', behavior: 'actionable', expiryDays: 7 },

  // Backend events - persistent
  'ai.analysis_complete': { persistent: true, priority: 'normal', defaultToastStyle: 'success', behavior: 'informational', expiryDays: 7 },
  'ai.analysis_failed': { persistent: true, priority: 'high', defaultToastStyle: 'error', behavior: 'actionable', expiryDays: 7 },
  'report.generated': { persistent: true, priority: 'normal', defaultToastStyle: 'success', behavior: 'informational', expiryDays: 30 },
  'report.failed': { persistent: true, priority: 'high', defaultToastStyle: 'error', behavior: 'actionable', expiryDays: 7 },
  'call.incoming': { persistent: true, priority: 'urgent', defaultToastStyle: 'info', behavior: 'actionable', expiryDays: 7 },
  'call.missed': { persistent: true, priority: 'high', defaultToastStyle: 'warning', behavior: 'actionable', expiryDays: 7 },
  'call.ended': { persistent: false, priority: 'low', defaultToastStyle: 'info', behavior: 'informational', expiryDays: 7 },

  // System alerts - persistent
  'quota.warning': { persistent: true, priority: 'high', defaultToastStyle: 'warning', behavior: 'actionable', expiryDays: 7 },
  'quota.exceeded': { persistent: true, priority: 'urgent', defaultToastStyle: 'error', behavior: 'actionable', expiryDays: 7 },
  'system.maintenance': { persistent: true, priority: 'normal', defaultToastStyle: 'warning', behavior: 'informational', expiryDays: 30 },
  'system.update': { persistent: true, priority: 'low', defaultToastStyle: 'info', behavior: 'informational', expiryDays: 30 },

  // Action required - persistent
  'approval.pending': { persistent: true, priority: 'high', defaultToastStyle: 'warning', behavior: 'actionable', expiryDays: null },
  'action.required': { persistent: true, priority: 'high', defaultToastStyle: 'warning', behavior: 'actionable', expiryDays: null },
};

/**
 * Get the rule for a notification event key.
 * Falls back to default rule if not found.
 */
export function getNotificationRule(eventKey: string): NotificationRule {
  return NOTIFICATION_RULES[eventKey] || {
    persistent: false,
    priority: 'normal',
    defaultToastStyle: 'info',
  };
}

/**
 * Default expiry in days per category.
 * null = never expires.
 */
export const CATEGORY_DEFAULT_EXPIRY_DAYS: Record<string, number | null> = {
  action_required: null,
  alert: 7,
  call: 7,
  report: 30,
  system: 30,
  comment: 14,
  mention: 14,
  ai_suggestion: 7,
  ai_analysis_complete: 7,
  achievement: 30,
};

/**
 * Default behavior per category.
 */
export const CATEGORY_DEFAULT_BEHAVIOR: Record<string, NotificationBehavior> = {
  action_required: 'actionable',
  alert: 'actionable',
  call: 'informational',      // low-priority call events default informational; high/urgent are actionable
  report: 'informational',
  system: 'informational',    // low-priority system notices default informational; high/urgent are actionable
  comment: 'actionable',
  mention: 'actionable',
  ai_suggestion: 'actionable',
  ai_analysis_complete: 'informational',
  achievement: 'informational',
};

/**
 * Compute the default behavior for a notification category and priority.
 * High/urgent call and system events stay actionable unless explicitly overridden.
 */
export function getDefaultBehavior(
  category: string,
  priority: NotificationPriority = 'normal',
): NotificationBehavior {
  if ((category === 'call' || category === 'system') && (priority === 'high' || priority === 'urgent')) {
    return 'actionable';
  }

  return CATEGORY_DEFAULT_BEHAVIOR[category] || 'actionable';
}

/**
 * Compute an ISO expiry date from a category, or null if it never expires.
 */
export function computeExpiryDate(category: string): string | null {
  const days = CATEGORY_DEFAULT_EXPIRY_DAYS[category];
  if (days == null) return null;
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

/**
 * Default rate limit configuration.
 */
export const DEFAULT_RATE_LIMITS: RateLimitConfig = {
  maxPerMinute: 10,
  maxPerCategory: 5,
  burstAllowance: 3,  // Urgent can exceed by 3
};

/**
 * Default deduplication window in milliseconds.
 */
export const DEFAULT_DEDUPE_WINDOW_MS = 5000;

/**
 * Deduplication windows by category (in milliseconds).
 * Longer windows for things that shouldn't spam.
 */
export const DEDUPE_WINDOWS: Record<string, number> = {
  'ai_analysis_complete': 60000,  // 1 min - don't spam analysis complete
  'report': 30000,                 // 30s - reports can come quickly
  'call': 10000,                   // 10s - calls are time-sensitive
  'alert': 300000,                 // 5 min - alerts should be rare
  'system': 600000,                // 10 min - system messages very rare
  'default': 5000,                 // 5s - default for most things
};

/**
 * Get the deduplication window for a category.
 */
export function getDedupeWindow(category: NotificationCategory): number {
  return DEDUPE_WINDOWS[category] || DEDUPE_WINDOWS.default;
}

/**
 * Toast duration by priority (in milliseconds).
 */
export const TOAST_DURATIONS: Record<NotificationPriority, number> = {
  low: 3000,      // 3 seconds
  normal: 4000,   // 4 seconds
  high: 6000,     // 6 seconds
  urgent: 8000,   // 8 seconds (or until dismissed)
};
